"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaArrowDown } from "react-icons/fa";
import { BrowserMockupCard } from "@/components/sections/browser-mockup-card";
import {
  GooglePositionProofCard,
  ImpressionsProofCard,
  QualifiedCallsProofCard,
} from "@/components/sections/dashboard-cards";
import { HERO_ENTRANCE_PERSPECTIVE } from "@/lib/hero-entrance-motion";
import {
  CONTAINER_BOUNCE_TRANSITION,
  createCardRailVariants,
  CARD_ORGANIZE_DELAYS,
  FINAL_CTA_DELAY,
  FINAL_GLOW_DELAY,
  getOrganizeCompleteMs,
  getOrganizeStartMs,
  INTRO_HIDDEN,
  INTRO_TRANSITION,
  INTRO_VISIBLE,
  MOCKUP_INTRO_HIDDEN,
  MOCKUP_INTRO_VISIBLE,
  MOCKUP_CENTER_OFFSET_MD,
  MOCKUP_CENTER_OFFSET_LG,
  ORGANIZE_TRANSITION,
  SCENE_CHAOS_POSE,
  SCENE_FINAL_POSE,
  SCENE_FINAL_TRANSITION,
  SEQUENCE_EASE,
} from "@/lib/transformation-organize-motion";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";

const APRES_IMAGE = "/assets/main/projets/après.png";

const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const MOCKUP_CARD_CLASS =
  "relative z-10 border border-white/25 ring-1 ring-[#d4d4d8]/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_30px_rgba(0,0,0,0.07),0_28px_70px_rgba(0,0,0,0.11),0_50px_120px_-30px_rgba(0,0,0,0.24)]";

const PANEL_SURFACE_CLASS =
  "pointer-events-none absolute inset-0 rounded-[1.75rem] bg-white/55 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.28),0_8px_24px_-12px_rgba(15,23,42,0.12)] ring-1 ring-white/80 backdrop-blur-md md:rounded-[2rem]";

const STAT_CARD_IDS = [
  { id: "impressions", Card: ImpressionsProofCard, delay: CARD_ORGANIZE_DELAYS[0] },
  { id: "google-position", Card: GooglePositionProofCard, delay: CARD_ORGANIZE_DELAYS[1] },
  { id: "calls", Card: QualifiedCallsProofCard, delay: CARD_ORGANIZE_DELAYS[2] },
] as const;

function useMockupCenterOffset() {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const md = window.matchMedia("(min-width: 768px)");
    const lg = window.matchMedia("(min-width: 1024px)");

    const update = () => {
      if (!md.matches) {
        setOffset(0);
        return;
      }

      setOffset(lg.matches ? MOCKUP_CENTER_OFFSET_LG : MOCKUP_CENTER_OFFSET_MD);
    };

    update();
    md.addEventListener("change", update);
    lg.addEventListener("change", update);
    return () => {
      md.removeEventListener("change", update);
      lg.removeEventListener("change", update);
    };
  }, []);

  return offset;
}

function TransformationHeader() {
  return (
    <div className="mx-auto w-full max-w-3xl text-center">
      <h2 className="mx-auto flex max-w-4xl items-center justify-center gap-3 text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:gap-4 sm:text-3xl md:text-4xl md:leading-[1.05]">
        <FaArrowDown className="shrink-0 translate-y-0.5 text-lg text-[#1C1C1C] sm:translate-y-1 sm:text-xl md:translate-y-1.5 md:text-2xl" aria-hidden="true" />
        <span>
          Ce qu&apos;on a fait pour{" "}
          <span className={HERO_GRADIENT_TEXT}>Star Limousine Paris</span>
        </span>
        <FaArrowDown className="shrink-0 translate-y-0.5 text-lg text-[#1C1C1C] sm:translate-y-1 sm:text-xl md:translate-y-1.5 md:text-2xl" aria-hidden="true" />
      </h2>
    </div>
  );
}

export function TransformationDashboard() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const reducedMotion = useReducedMotion();
  const mockupCenterOffset = useMockupCenterOffset();
  const [isOrganized, setIsOrganized] = useState(false);
  const [showFinal, setShowFinal] = useState(false);

  const showOrganized = reducedMotion ? isInView : isOrganized;
  const showFinalState = reducedMotion ? isInView : showFinal;
  const motionPhase = !isInView ? "hidden" : showOrganized ? "organized" : "chaos";

  useEffect(() => {
    if (!isInView) return;

    if (reducedMotion) {
      setIsOrganized(true);
      setShowFinal(true);
      return;
    }

    const organizeTimer = window.setTimeout(() => setIsOrganized(true), getOrganizeStartMs());
    const finalTimer = window.setTimeout(() => setShowFinal(true), getOrganizeCompleteMs());

    return () => {
      window.clearTimeout(organizeTimer);
      window.clearTimeout(finalTimer);
    };
  }, [isInView, reducedMotion]);

  return (
    <div
      ref={sectionRef}
      className="relative mx-auto w-full max-w-[960px]"
      style={{ perspective: HERO_ENTRANCE_PERSPECTIVE, perspectiveOrigin: "50% 42%" }}
    >
      <motion.div
        className="pointer-events-none absolute -inset-x-10 -inset-y-6 rounded-[2.5rem] bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.14)_0%,transparent_72%)] blur-3xl"
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={showFinalState ? { opacity: 1 } : { opacity: 0 }}
        transition={{
          duration: reducedMotion ? 0.2 : 0.38,
          ease: SEQUENCE_EASE,
          delay: reducedMotion || !showFinalState ? 0 : FINAL_GLOW_DELAY,
        }}
      />

      <motion.div
        className="relative"
        animate={showFinalState ? SCENE_FINAL_POSE : SCENE_CHAOS_POSE}
        transition={reducedMotion ? { duration: 0.2, ease: "easeOut" } : SCENE_FINAL_TRANSITION}
        style={{ transformStyle: "preserve-3d" }}
      >
        <motion.div
          className="mb-6 sm:mb-8"
          initial={reducedMotion ? { opacity: 0 } : INTRO_HIDDEN}
          animate={isInView ? (reducedMotion ? { opacity: 1 } : INTRO_VISIBLE) : reducedMotion ? { opacity: 0 } : INTRO_HIDDEN}
          transition={reducedMotion ? { duration: 0.2, ease: "easeOut" } : INTRO_TRANSITION}
        >
          <TransformationHeader />
        </motion.div>

        <div className="relative overflow-visible" style={{ transformStyle: "preserve-3d" }}>
          <motion.div
            className={PANEL_SURFACE_CLASS}
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={
              showFinalState
                ? { opacity: 1, scale: [0.9, 1.05, 1] }
                : { opacity: 0, scale: 0.9 }
            }
            transition={reducedMotion ? { duration: 0.2, ease: "easeOut" } : CONTAINER_BOUNCE_TRANSITION}
            style={{ transformOrigin: "50% 50%" }}
          />

          <div className="relative overflow-visible p-3 sm:p-4 md:p-4 lg:p-5">
            <div
              className="relative flex min-h-[280px] flex-col gap-4 overflow-visible sm:min-h-[300px] md:min-h-[360px] md:grid md:grid-cols-[minmax(0,1fr)_172px] md:items-stretch md:gap-4 lg:grid-cols-[minmax(0,1fr)_200px]"
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.div
                className="relative z-10 flex min-h-0 w-full flex-col md:h-full"
                initial={reducedMotion ? { opacity: 0 } : MOCKUP_INTRO_HIDDEN}
                animate={
                  isInView
                    ? reducedMotion
                      ? { opacity: 1 }
                      : MOCKUP_INTRO_VISIBLE
                    : reducedMotion
                      ? { opacity: 0 }
                      : MOCKUP_INTRO_HIDDEN
                }
                transition={reducedMotion ? { duration: 0.2, ease: "easeOut" } : INTRO_TRANSITION}
              >
                <motion.div
                  className="flex h-full min-h-0 flex-col"
                  initial={false}
                  animate={{ x: showOrganized ? 0 : mockupCenterOffset }}
                  transition={showOrganized ? ORGANIZE_TRANSITION : { duration: 0 }}
                >
                  <BrowserMockupCard
                    mockupOnly
                    hideLabel
                    label="Après"
                    imageSrc={APRES_IMAGE}
                    imageAlt="Aperçu du site Star Limousine Paris après transformation WebDifference"
                    cardClassName={`${MOCKUP_CARD_CLASS} flex h-full min-h-0 flex-col`}
                    browserUrl="www.star-limousine-paris.com"
                    bullets={[]}
                    viewportClassName="h-[200px] w-full sm:h-[240px] md:min-h-0 md:flex-1"
                    imageSizes="(max-width: 768px) 92vw, 640px"
                  />
                </motion.div>
              </motion.div>

              <div className="relative flex w-full flex-col gap-3 overflow-visible px-1 py-1 md:w-auto md:self-start">
                {STAT_CARD_IDS.map(({ id, Card, delay }) => (
                  <motion.div
                    key={id}
                    variants={createCardRailVariants(!!reducedMotion, delay)}
                    initial="hidden"
                    animate={motionPhase}
                    className="overflow-visible"
                    style={{ transformOrigin: "0% 50%" }}
                  >
                    <Card className="!w-full" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <motion.div
          className="mt-6 flex justify-center sm:mt-8"
          initial={{ opacity: 0, y: 10 }}
          animate={showFinalState ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{
            duration: reducedMotion ? 0.2 : 0.35,
            ease: SEQUENCE_EASE,
            delay: reducedMotion || !showFinalState ? 0 : FINAL_CTA_DELAY,
          }}
        >
          <HeroCtaButton>Voir comment on fait ça pour vous</HeroCtaButton>
        </motion.div>
      </motion.div>
    </div>
  );
}
